import { useEffect, useState } from "react";
import { api, adminHeaders } from "@/lib/apiClient";
import { useAdmin } from "@/context/AdminContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, Plus, Trash2, Check, X, Pencil, Users, Eye, EyeOff, KeyRound, Mail, Save } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

export default function Gestione() {
  const { isAdmin, login, logout } = useAdmin();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showChange, setShowChange] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(password);
      toast.success("Modalità modifica attivata");
      setPassword("");
    } catch (err) {
      toast.error("Password non valida");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    try {
      await api.post("/auth/forgot-password", {});
      toast.success("Se un indirizzo è configurato, riceverai un'email con il link per reimpostare la password.");
    } catch (err) {
      toast.error("Errore nell'invio dell'email");
    }
  };

  if (!isAdmin) {
    return (
      <div data-testid="gestione-page">
        <PageHeader title="Gestione" subtitle="Area protetta da password" />
        <div className="border border-border bg-card p-8 sm:p-10 max-w-lg">
          <div className="h-14 w-14 bg-rugby-yellow/10 border border-rugby-yellow/30 flex items-center justify-center mb-5">
            <Lock className="h-6 w-6 text-rugby-yellow" />
          </div>
          <h3 className="font-head uppercase tracking-tight text-2xl font-bold mb-2">Sblocca Modifica</h3>
          <p className="text-muted-foreground text-sm mb-6">
            Inserisci la password per abilitare la modifica di atleti e tabelle.
          </p>
          <form onSubmit={unlock} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                data-testid="admin-password-input"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black/50 border-border focus-visible:ring-rugby-yellow pr-10"
                autoFocus
              />
              <button
                type="button"
                data-testid="toggle-admin-password"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-rugby-yellow"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              type="submit"
              data-testid="admin-password-submit"
              disabled={loading}
              className="w-full bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
            >
              {loading ? "Verifica..." : "Sblocca"}
            </Button>
          </form>
          <button
            type="button"
            data-testid="open-change-password"
            onClick={() => setShowChange(true)}
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-muted-foreground hover:text-rugby-yellow transition-colors"
          >
            <KeyRound className="h-4 w-4" /> Reimposta password
          </button>
          <button
            type="button"
            data-testid="forgot-password-btn"
            onClick={forgotPassword}
            className="mt-4 sm:ml-6 inline-flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-muted-foreground hover:text-rugby-yellow transition-colors"
          >
            <Mail className="h-4 w-4" /> Password dimenticata?
          </button>
        </div>
        <ChangePasswordDialog open={showChange} onOpenChange={setShowChange} />
      </div>
    );
  }

  return (
    <div data-testid="gestione-page">
      <PageHeader title="Gestione Tabelle" subtitle="Modifica atleti, tipi di attività e tipi di presenza">
        <Button
          onClick={() => setShowChange(true)}
          data-testid="admin-change-password-btn"
          variant="outline"
          className="border-border hover:border-rugby-yellow hover:text-rugby-yellow font-head uppercase tracking-wide text-xs font-bold"
        >
          <KeyRound className="h-4 w-4 mr-2" /> Reimposta Password
        </Button>
        <Button
          onClick={logout}
          data-testid="admin-logout-btn"
          variant="outline"
          className="border-rugby-red/40 text-rugby-red hover:bg-rugby-red/10 font-head uppercase tracking-wide text-xs font-bold"
        >
          <Lock className="h-4 w-4 mr-2" /> Blocca Modifica
        </Button>
      </PageHeader>
      <ChangePasswordDialog open={showChange} onOpenChange={setShowChange} />
      <Tabs defaultValue="attivita">
        <TabsList className="bg-card border border-border mb-6">
          <TabsTrigger value="attivita" data-testid="tab-tipo-attivita" className="font-head uppercase tracking-wide data-[state=active]:bg-rugby-yellow data-[state=active]:text-black">
            Tipo Attività
          </TabsTrigger>
          <TabsTrigger value="presenza" data-testid="tab-tipo-presenza" className="font-head uppercase tracking-wide data-[state=active]:bg-rugby-yellow data-[state=active]:text-black">
            Tipo Presenza
          </TabsTrigger>
          <TabsTrigger value="atleti" data-testid="tab-atleti" className="font-head uppercase tracking-wide data-[state=active]:bg-rugby-yellow data-[state=active]:text-black">
            Atleti
          </TabsTrigger>
          <TabsTrigger value="impostazioni" data-testid="tab-impostazioni" className="font-head uppercase tracking-wide data-[state=active]:bg-rugby-yellow data-[state=active]:text-black">
            Impostazioni
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attivita">
          <SimpleTableManager endpoint="activity-types" title="Tipi di Attività" />
        </TabsContent>
        <TabsContent value="presenza">
          <SimpleTableManager endpoint="presence-types" title="Tipi di Presenza" />
        </TabsContent>
        <TabsContent value="atleti">
          <AthletesManager navigate={navigate} />
        </TabsContent>
        <TabsContent value="impostazioni">
          <SettingsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleTableManager({ endpoint, title }) {
  const [items, setItems] = useState([]);
  const [newVal, setNewVal] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const add = async () => {
    if (!newVal.trim()) return;
    await api.post(`/${endpoint}`, { value: newVal.trim() }, { headers: adminHeaders() });
    setNewVal("");
    toast.success("Aggiunto");
    load();
  };
  const saveEdit = async (id) => {
    await api.put(`/${endpoint}/${id}`, { value: editVal.trim() }, { headers: adminHeaders() });
    setEditId(null);
    toast.success("Aggiornato");
    load();
  };
  const remove = async (id) => {
    await api.delete(`/${endpoint}/${id}`, { headers: adminHeaders() });
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="border border-border bg-card max-w-xl">
      <div className="px-5 py-4 border-b border-border font-head uppercase tracking-wide font-bold">{title}</div>
      <div className="p-5 flex gap-2 border-b border-border">
        <Input
          data-testid={`${endpoint}-new-input`}
          placeholder="Nuovo valore..."
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
        />
        <Button data-testid={`${endpoint}-add-btn`} onClick={add} className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase font-bold">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items.map((it) => (
        <div key={it.id} data-testid={`${endpoint}-row-${it.id}`} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0">
          {editId === it.id ? (
            <>
              <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="bg-black/50 border-border h-8 mr-2" />
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => saveEdit(it.id)}><Check className="h-4 w-4 text-emerald-400" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
              </div>
            </>
          ) : (
            <>
              <span className="font-body">{it.value}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditId(it.id); setEditVal(it.value); }}><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(it.id)}><Trash2 className="h-4 w-4 text-rugby-red" /></Button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function AthletesManager({ navigate }) {
  const [athletes, setAthletes] = useState([]);
  const [newA, setNewA] = useState({ cognome: "", nome: "", anno_nascita: "", societa: "", tesserato: "NO" });

  const load = () => api.get("/athletes").then((r) => setAthletes(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newA.cognome.trim()) { toast.error("Inserisci almeno il cognome"); return; }
    await api.post("/athletes", newA, { headers: adminHeaders() });
    setNewA({ cognome: "", nome: "", anno_nascita: "", societa: "", tesserato: "NO" });
    toast.success("Atleta aggiunto");
    load();
  };
  const remove = async (id) => {
    await api.delete(`/athletes/${id}`, { headers: adminHeaders() });
    toast.success("Atleta eliminato");
    load();
  };

  return (
    <div className="border border-border bg-card">
      <div className="px-5 py-4 border-b border-border font-head uppercase tracking-wide font-bold flex items-center gap-2">
        <Users className="h-4 w-4 text-rugby-yellow" /> Atleti ({athletes.length})
      </div>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-6 gap-2 border-b border-border">
        <Input data-testid="new-athlete-cognome" placeholder="Cognome" value={newA.cognome} onChange={(e) => setNewA({ ...newA, cognome: e.target.value })} className="bg-black/50 border-border sm:col-span-2" />
        <Input data-testid="new-athlete-nome" placeholder="Nome" value={newA.nome} onChange={(e) => setNewA({ ...newA, nome: e.target.value })} className="bg-black/50 border-border" />
        <Input placeholder="Anno" value={newA.anno_nascita} onChange={(e) => setNewA({ ...newA, anno_nascita: e.target.value })} className="bg-black/50 border-border" />
        <Input placeholder="Società" value={newA.societa} onChange={(e) => setNewA({ ...newA, societa: e.target.value })} className="bg-black/50 border-border" />
        <Button data-testid="add-athlete-btn" onClick={add} className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase font-bold">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </div>
      <div className="max-h-[500px] overflow-y-auto">
        {athletes.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0 hover:bg-white/5">
            <div className="cursor-pointer" onClick={() => navigate(`/atleti/${a.id}`)}>
              <span className="font-head font-semibold uppercase text-sm hover:text-rugby-yellow transition-colors">{a.cognome}</span>{" "}
              <span className="text-muted-foreground text-sm">{a.nome}</span>
              <span className="text-muted-foreground text-xs ml-2">{a.societa}</span>
            </div>
            <Button size="sm" variant="ghost" data-testid={`delete-athlete-${a.id}`} onClick={() => remove(a.id)}>
              <Trash2 className="h-4 w-4 text-rugby-red" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsManager() {
  const [email, setEmail] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [notify, setNotify] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/settings", { headers: adminHeaders() }).then((r) => {
      setEmail(r.data.reset_email || "");
      setExpiry(String(r.data.reset_expiry_minutes || 30));
      setNotify(r.data.notify_email || "");
    });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (email && !email.includes("@")) {
      toast.error("Indirizzo email non valido");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        "/settings",
        { reset_email: email.trim(), reset_expiry_minutes: parseInt(expiry, 10) },
        { headers: adminHeaders() }
      );
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-card max-w-xl" data-testid="settings-manager">
      <div className="px-5 py-4 border-b border-border font-head uppercase tracking-wide font-bold flex items-center gap-2">
        <Mail className="h-4 w-4 text-rugby-yellow" /> Reset Password via Email
      </div>
      <div className="p-5 space-y-5">
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Email che riceve il link di reset
          </Label>
          <Input
            data-testid="settings-reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@dominio.it"
            className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Durata di validità del link
          </Label>
          <Select value={expiry} onValueChange={setExpiry}>
            <SelectTrigger data-testid="settings-expiry" className="bg-black/50 border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 minuti</SelectItem>
              <SelectItem value="60">1 ora</SelectItem>
              <SelectItem value="1440">24 ore</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground border-t border-border pt-4">
          L'email di <b>conferma cambio password</b> viene sempre inviata a:{" "}
          <span className="text-rugby-yellow font-mono">{notify || "—"}</span>
        </div>
        <Button
          onClick={save}
          data-testid="settings-save-btn"
          disabled={saving}
          className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
        >
          <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Impostazioni"}
        </Button>
      </div>
    </div>
  );
}
